import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import {
  assert_array_not_empty,
  assert_false,
  assert_string_not_empty,
  assert_string_object_id_not_empty,
  assert_type_enum,
  not_implemented,
} from '@shared/asserts/utils_asserts';
import { assert_string_uuid_not_empty } from '@shared/asserts/utils_asserts_string';
import { DTO__base } from '@shared/common/DTO__base';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { ENUM__ml_asset_family } from '@shared/dto/+model/sd/_enums/ENUM__ml_asset_family';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { MAP__fields_by_role } from '@shared/types/types';
import { DTO_GRAPH__instance } from '../../_graph/DTO_GRAPH__instance';
import { ENUM__invocation_type } from '../../_invocations/_base/ENUM__invocation_type';
import { FIELD_INITIALIZER__base } from '../../_invocations/_base/FIELD_INITIALIZER__base';
import { CLASSES_TESTED } from '../_primitives/_base/DECORATOR__class_serialization_tested';
import { CLASSES_TESTED_FOR_HASH } from '../_primitives/_base/DECORATOR__class_serialization_tested_for_hash';
import { DECORATOR__field_not_linked } from '../_primitives/_base/DECORATOR__field_not_linked';
import { ENUM__field_graph_interaction } from './_enums/ENUM__field_graph_interaction';
import { ENUM__field_connectable_type } from './_methods/ENUM__field_connectable_type';
import { FN_FIELD__fields_types_are_compatible } from './_methods/FN_FIELD__fields_types_are_compatible';
import { FN_FIELD_CONTAINER__get_fields } from './_methods/FN_FIELD_CONTAINER__get_fields';
import { FN_FIELD_CONTAINER__init_as_default } from './_methods/FN_FIELD_CONTAINER__init_as_default';
import { DTO_META__field_info } from './DTO_META__field_info';

export class FIELD__base extends FIELD_INITIALIZER__base {
  /*
   * STATIC FIELDS
   */

  static _class_name_static = ENUM__dto_class_name.ABSTRACT;

  /*
   * STATIC METHODS
   */
  static map_by_graph_id<T extends FIELD__base>(fields: T[]) {
    let out = new Map<string, T[]>();

    for (let field of fields) {
      assert_string_object_id_not_empty(field.meta.graph_id);
      out.add_item_to_array(field.meta.graph_id, field);
    }

    return out;
  }

  static map_by_field_id<T extends FIELD__base>(fields: T[]) {
    let out = new MAP__serializable<string, T>();

    for (let field of fields) {
      assert_string_uuid_not_empty(field.meta.field_id);
      out.set(field.meta.field_id, field);
    }

    return out;
  }

  static map_by_field_role<T extends FIELD__base>(fields: T[]): MAP__fields_by_role {
    let out = new MAP__serializable<string, T>();

    for (let field of fields) out.set(field.meta.field_role, field);

    return out;
  }

  static create_field_container_default<T extends FIELD__base>(this: new () => T, overrides: Partial<T> = {}): T {
    // let constructor = this as unknown as CONSTRUCTOR_FIELD_CONTAINER<T>;

    let out = new this() as T;
    // out.meta = new DTO_FIELD_META__info().clone(constructor.get_meta_params_default());

    // FN_FIELD_CONTAINER__init_stubs(out);
    FN_FIELD_CONTAINER__init_as_default(out);

    out = out.clone({ ...overrides });

    return out;
  }

  static get_meta_params_default(): Partial<DTO_META__field_info> {
    return {
      //
      grid2d__can_be_dimension: false,
      grid2d__is_row: false,
      grid2d__is_column: false,

      //
      ui__is_visible: false,
      ui__has_component: true,

      //
      graph__interaction: ENUM__field_graph_interaction.VISIBLE_CONNECTABLE,

      //
      field_hash__calculated: false,
      field_hash__need_include_path: true,

      //
      field_hash__value_full: ENUM__string_id_empty.HASH_SHA256,
      field_hash__value_clean: ENUM__string_id_empty.HASH_SHA256,
    };
  }

  static get_map_by_path<T extends FIELD__base>(fields: T[]): MAP__serializable<string, T> {
    let out = new MAP__serializable<string, T>();

    for (const field of fields) {
      assert_false(out.has(field.meta.field_path), { assert_type: ENUM__error_type.VALIDATION_ITEM_ALREADY_EXISTS, field_path: field.meta.field_path });

      out.set(field.meta.field_path, field);
    }

    return out;
  }

  /*
   * SORT
   */
  static sort_group_by_field_group(group_a: [string, FIELD__base[]], group_b: [string, FIELD__base[]]) {
    let [field_group_a, group_a_items] = group_a;
    let [field_group_b, group_b_items] = group_b;

    if (field_group_a === '') return -1;
    if (field_group_b === '') return 1;

    return field_group_a.localeCompare(field_group_b);
  }

  static sort_group_by_sort_criteria(field_a: FIELD__base, field_b: FIELD__base) {
    let sort_criteria_a = field_a.meta.sort_criteria.only_alphanumeric().toLocaleLowerCase();

    let sort_criteria_b = field_b.meta.sort_criteria.only_alphanumeric().toLocaleLowerCase();

    return sort_criteria_a.localeCompare(sort_criteria_b, undefined, { numeric: true, sensitivity: 'base' });
  }

  static sort_group_by_node_type(group_a: [string, FIELD__base[]], group_b: [string, FIELD__base[]]) {
    let [node_id_a, group_a_items] = group_a;
    let [node_id_b, group_b_items] = group_b;

    assert_array_not_empty(group_a_items);
    assert_array_not_empty(group_b_items);

    let [item_a, item_b] = [group_a_items[0], group_b_items[0]];

    return item_a.meta.node_type.localeCompare(item_b.meta.node_type);
  }

  static sort_by_name(a: FIELD__base, b: FIELD__base) {
    return a.meta.field_name.localeCompare(b.meta.field_name);
  }

  /*
   * REGISTER
   */

  // static register_input_backup<T_FIELD extends FIELD__base>(
  //   this: new () => T_FIELD,
  //   options_field: Partial<T_FIELD> = {},
  //   options_meta: Partial<DTO_FIELD_META__info> = {},
  // ) {
  //   options_meta = { ...options_meta, ui_is_readonly: false, kind: ENUM__field_kind.INPUT };

  //   let class_object = this as CONSTRUCTOR_FIELD_BASE<T_FIELD>;

  //   options_meta = { ...class_object.get_meta_params_default(), ...options_meta };

  //   options_meta = new DTO_FIELD_META__info().clone(options_meta);

  //   return class_object.register_field(options_field, options_meta);
  // }

  // static register_input_optional_backup<T_FIELD extends FIELD__base>(
  //   this: new () => T_FIELD,
  //   options_field: Partial<T_FIELD> = {},
  //   options_meta: Partial<DTO_FIELD_META__info> = {},
  // ) {
  //   options_meta = { ...options_meta, is_optional: true };

  //   let class_object = this as CONSTRUCTOR_FIELD_BASE<T_FIELD>;

  //   return class_object.register_input(options_field, options_meta);
  // }

  // static register_output_backup<T_FIELD extends FIELD__base>(
  //   this: new () => T_FIELD,
  //   options_field: Partial<T_FIELD> = {},
  //   options_meta: Partial<DTO_FIELD_META__info> = {},
  // ) {
  //   let class_object = this as CONSTRUCTOR_FIELD_BASE<T_FIELD>;

  //   options_meta = { ...options_meta, ui_is_readonly: true, kind: ENUM__field_kind.OUTPUT };

  //   options_meta = { ...class_object.get_meta_params_default(), ...options_meta };

  //   options_meta = new DTO_FIELD_META__info().clone(options_meta);

  //   return class_object.register_field(options_field, options_meta);
  // }

  // static register_field_backup<T_FIELD extends FIELD__base>(
  //   this: new () => T_FIELD,
  //   options_field: Partial<T_FIELD> = {},
  //   options_meta: Partial<DTO_FIELD_META__info> = {},
  // ) {
  //   const class_object = this as unknown as CONSTRUCTOR_FIELD_CONTAINER<T_FIELD>;

  //   return function (value: any, context: ClassFieldDecoratorContext) {
  //     // 1. Force the metadata to exist if it's missing (though context should provide it)
  //     const metadata = context.metadata;

  //     const field_metadata = new REFLECT_METADATA<T_FIELD>();
  //     field_metadata.options_field = cloneDeep(options_field);
  //     field_metadata.options_meta = cloneDeep(options_meta);
  //     field_metadata.class_object = class_object as unknown as CONSTRUCTOR_FIELD_CONTAINER<T_FIELD>;

  //     metadata[context.name.toString()] = field_metadata;

  //     return (initial_value: any) => initial_value;
  //   };
  // }

  /*
   * FIELDS
   */

  meta: DTO_META__field_info;

  /*
   * PROPERTIES
   */

  @DECORATOR__field_not_linked()
  has_effect_on_generation_runtime(graph: DTO_GRAPH__instance) {
    return this.meta.generation__has_effect_static;
  }

  get grid2d_dimension_values(): any[] {
    not_implemented();
    return [];
  }

  get_grid2d_side_size() {
    return this.grid2d_dimension_values.length;
  }

  field_connectable__value(graph: DTO_GRAPH__instance): string {
    not_implemented();
  }

  field_connectable__type(): ENUM__field_connectable_type {
    not_implemented();
    return '' as ENUM__field_connectable_type;
  }

  /*
   * STATIC METHODS
   */

  /*
   * PROPERTIES
   */

  get graph_field__height() {
    return 1;
  }

  /*
   * EVENTS
   */

  /*
   * METHODS
   */

  constructor() {
    super();

    this.meta = DTO_META__field_info.CREATE_STUB();
  }

  post_init() {}

  update_fields_aliases(fields_map: MAP__fields_by_role) {}

  try_set_field_role(fields_map: MAP__fields_by_role, field: FIELD__base, role: string) {
    if (fields_map.has(role)) return;

    field.meta.field_role = role;

    fields_map.set(role, field);
  }

  prepare_for_usage_in(graph: DTO_GRAPH__instance) {}

  grid2d_dimension__set_value(value: any) {
    not_implemented();
  }

  grid2d_dimension__apply_for(graph: DTO_GRAPH__instance) {
    not_implemented();
  }

  is_equal(other: FIELD__base) {
    if (this.is_stub() == other.is_stub() && this.is_stub()) return true;
    if (this.is_stub() != other.is_stub()) return false;

    if (this.field_connectable__type() != other.field_connectable__type()) return false;
    if (this.meta.node_id != other.meta.node_id) return false;
    if (this.meta.field_path != other.meta.field_path) return false;
    if (this.meta.field_hash__value_full != other.meta.field_hash__value_full) return false;
    if (this.meta.field_id != other.meta.field_id) return false;

    return true;
  }

  is_ready_for_manual_edit() {
    if (this.meta.data__is_linked) return false;
    if (this.meta.kind__is_output) return false;
    if (this.meta.ui__is_readonly) return false;

    return true;
  }

  toString(): string {
    let out = DTO__base.prototype.toString.call(this);

    out += ` ${this.meta.node_type}.${this.meta.field_path}`;

    return out;
  }

  fill_field_name_and_path(field_name: string, path: string) {
    [this.meta.field_name, this.meta.field_path] = [field_name, path];

    for (const [field_name, field] of Object.entries(this)) {
      if (!(field instanceof FIELD__base)) continue;

      field.fill_field_name_and_path(field_name, path + '.' + field_name);
    }
  }

  id_create() {
    this.meta.id_create();

    let children = FN_FIELD_CONTAINER__get_fields(this);

    for (let field of children) field.id_create();
  }

  copy_meta_from_parent(parent: FIELD__base) {
    this.meta.copy_meta_from_parent(parent.meta);
  }

  /*
   * HASH
   */

  get hash_base_values() {
    let out: string[] = [];

    if (this.meta.field_hash__need_include_path) {
      assert_string_not_empty(this.meta.field_path);
      out.push(`field__path=${this.meta.field_path}`);
    }

    assert_type_enum(this.meta.node_type, ENUM__invocation_type);
    out.push(`node__type=${this.meta.node_type}`);

    assert_type_enum(this.meta.node_family, ENUM__ml_asset_family);
    out.push(`node__family=${this.meta.node_family}`);

    return out;
  }

  data_reset(graph: DTO_GRAPH__instance) {}

  /*
   * VALIDATION
   */

  find_source(graph: DTO_GRAPH__instance): this {
    if (!this.meta.data__is_linked) return this;

    let out = graph.field__get_source_for(this);

    return out as this;
  }

  is_field_is_compatible_to_sync_with(other: FIELD__base): boolean {
    return FN_FIELD__fields_types_are_compatible(this, other);
  }

  assert_standard_fields(): void {
    if (this.meta.grid2d__is_dimension) assert_array_not_empty(this.grid2d_dimension_values);

    if (!this.meta.kind__is_output && !this.meta.data__is_linked) this.assert_standard_fields_with_value();
  }

  assert_standard_fields_with_value(): void {
    //do nothing
  }

  get_is_tested_for_hash() {
    return CLASSES_TESTED_FOR_HASH.includes(this.constructor);
  }

  get_is_tested() {
    return CLASSES_TESTED.includes(this.constructor);
  }
}
